import React, { useEffect, useState } from 'react';
import { Icon } from 'components/Icon';
import {
  HeadingTypography,
  PreRegAutoComplete,
  StyledDatePicker,
  StyledTimePicker,
  StyledTextArea,
  TimeClock,
  StyledSelect,
  PreRegWidth
} from './styled';
import { useHistory } from 'react-router-dom';
import { FontSize, IconSize, Spacing } from 'modules/styles/variables';
import { PersonalInfoInputLabel } from './PersonalInformation/styled';
import {
  KeyboardDatePickerProps,
  MuiTextField,
  MuiFormControl,
  TimePickerProps,
  SelectProps,
  StandardTextFieldProps
} from 'theme/material-ui';
import Spacer from 'components/UI/Layout/Spacer';
import { ProviderSummary } from 'store/findProvider/types';
// import { basicProviderSearch, reset } from 'shared/src/store/providerSearch/search/actions'; TODO: When we know the API
// import { useDispatch } from 'react-redux'; TODO: When we know the API
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { FlexBoxRow, FlexProps } from 'components/UI/Layout/FlexBox';
import ConfirmModal, { ConfirmModalProps } from 'components/UI/Modals/ConfirmModal';
import { InfoButton } from 'components/PreRegister/InfoButton';
import { Color } from 'modules/styles/colors';
import Typography from 'components/UI/Typography';
import { PreRegAdditionalInfoText } from './constant';
import { OnBlurType, OnChangeType } from 'types';
import { StyledAsterisk } from 'components/RequiredFieldsLegend';

export function HeadingMuiBox(props: FlexProps) {
  return <FlexBoxRow {...props} padding={Spacing.medium} alignItems="center" />;
}

interface PreRegHeaderProps {
  title: string;
  ['data-testid']?: string;
  handleClose?: () => void;
}
export function PreRegHeader(props: PreRegHeaderProps) {
  const { title, handleClose } = props;
  const history = useHistory();

  return (
    <HeadingMuiBox data-testid={props['data-testid']}>
      <HeadingTypography fontSize={FontSize.heading} data-testid="guarantor-header-label">
        {title}
      </HeadingTypography>
      <Icon
        data-testid="close"
        name="close"
        onClick={() => (handleClose ? handleClose() : history.push(`/pre-reg/checklist`))}
        size={IconSize.large}
      />
    </HeadingMuiBox>
  );
}

export function PreRegInfoLabel({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <PersonalInfoInputLabel>{label}</PersonalInfoInputLabel>
      <p>{value}</p>
    </div>
  );
}

/**
 * Method: PreRegSelectProvider
 * Description: Physician Lookup
 *
 * Notes: We should be getting the options from a new Cerner api that is not implemented. As a stopgap I am using basicProviderSearch.
 * TODO: replace basicProviderSearch with real search when it is available.
 */
export function PreRegSelectProvider({
  defaultValue,
  isFetching,
  label,
  name,
  providers,
  required,
  setFieldValue
}: {
  defaultValue: string;
  isFetching: boolean;
  label: string;
  name: string;
  providers: ProviderSummary[];
  required?: boolean;
  setFieldValue: (name: string, value?: string) => void;
}) {
  // const dispatch = useDispatch(); TODO: When we know the API
  const [lastCall, setLastCall] = useState('');
  const [nextCall, setNextCall] = useState('');

  const handleTextChange = (str: string) => {
    setNextCall(str);
    if (str.length <= 3 && providers.length) {
      // dispatch(reset()); TODO: When we know the API
    } else if (str.length > 3 && !isFetching) {
      setLastCall(str);
      // dispatch(basicProviderSearch({ input: str, limit: 5, page: 1 })); TODO: When we know the API
    }
  };

  useEffect(() => {
    if (!isFetching && nextCall.length > 3 && lastCall !== nextCall) {
      setLastCall(nextCall);
      // dispatch(basicProviderSearch({ input: nextCall, limit: 5, page: 1 })); TODO: When we know the API
    }
  }, [isFetching]);

  return (
    <div data-testid="pre-reg-select-provider">
      <PersonalInfoInputLabel>
        {label}
        {required ? <StyledAsterisk /> : null}
      </PersonalInfoInputLabel>
      <Spacer spacing="small" />
      <PreRegAutoComplete
        defaultValue={defaultValue}
        onChange={(evt, value) => {
          if (!value || typeof value === 'string') {
            setFieldValue(name, value);
          } else {
            // TODO: When the final contract is decided we can provide "ncid", "npi", or "providerId" from this object.
            setFieldValue(name, value.displayName);
          }
        }}
        data-testid={`${name}-autocomplete`}
        forcePopupIcon
        popupIcon={<KeyboardArrowDownIcon />}
        clearOnEscape
        options={providers}
        getOptionLabel={(option: ProviderSummary | string) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.displayName;
        }}
        noOptionsText="Please type to select the name of your provider"
        filterOptions={options => options}
        renderInput={params => (
          <MuiTextField
            data-testid={`pre-reg-select-provider-text-field-${name}`}
            {...params}
            name={name}
            variant="outlined"
            fullWidth
            onChange={evt => handleTextChange(evt.target.value)}
            onBlur={evt => {
              // dispatch(reset()); TODO: When we know the API
            }}
          />
        )}
      />
    </div>
  );
}

interface PreRegDatePickerProps extends KeyboardDatePickerProps {
  name: string;
  label: string;
  disableToolbar?: boolean;
}

export function PreRegDatePicker(props: PreRegDatePickerProps) {
  const { label, disableToolbar = true, ...rest } = props;
  return (
    <>
      <PersonalInfoInputLabel>
        {label}
        {props.required ? <StyledAsterisk /> : null}
      </PersonalInfoInputLabel>
      <StyledDatePicker
        data-testid={`pre-reg-date-picker-${rest.name}`}
        disableFuture
        autoOk
        hideAsterisk
        disableToolbar={disableToolbar}
        variant="inline"
        format="MM/DD/YYYY"
        margin="none"
        label="mm/dd/yyyy"
        KeyboardButtonProps={{
          'aria-label': 'change date'
        }}
        {...rest}
      />
      <Spacer spacing="large" />
    </>
  );
}

interface PreRegTimePickerProps extends TimePickerProps {
  name: string;
}
export function PreRegTimePicker(props: PreRegTimePickerProps) {
  const id = `StyledTimePicker-${props.name}`;
  return (
    <div>
      <StyledTimePicker
        id={id}
        data-testid={id}
        margin="none"
        {...props}
        label="HH:MM"
      ></StyledTimePicker>
      <TimeClock onClick={() => document.getElementById(id)?.click()} />
    </div>
  );
}

export interface PreRegTextAreaProps extends StandardTextFieldProps {
  maxLength: number;
  name: string;
  title: string;
  handleRequestOpen?: () => void;
  ['data-testid']?: string;
  value?: string;
}

export function PreRegTextArea(props: PreRegTextAreaProps) {
  const { title, handleRequestOpen, value, onChange, maxLength, name, ...rest } = props;
  const valueLen = value?.length || 0;
  const trueValue = value && valueLen > maxLength ? value.substring(0, maxLength) : value;

  return (
    <>
      <PersonalInfoInputLabel>
        {title}
        <StyledAsterisk />
        {handleRequestOpen ? (
          <InfoButton handleRequestOpen={handleRequestOpen} hideBorder color={Color.blueHue2} />
        ) : null}
      </PersonalInfoInputLabel>
      <StyledTextArea
        data-testid={props['data-testid'] || `pre-reg-text-area-${name}`}
        name={name}
        value={trueValue}
        onChange={onChange}
        variant="outlined"
        {...rest}
      />
      <PreRegWidth>
        <Typography textAlign="right">
          {trueValue?.length
            ? `${maxLength - trueValue?.length} Remaining`
            : `${maxLength} ${PreRegAdditionalInfoText.maxText}`}
        </Typography>
      </PreRegWidth>
      <Spacer spacing="large" />
    </>
  );
}

export function PreRegConfirmModal(props: ConfirmModalProps) {
  return (
    <ConfirmModal
      showCloseIcon
      maxWidth="400px"
      fullWidth
      position="bottom"
      style={{ padding: 0 }}
      {...props}
    />
  );
}

export interface PreRegSelectProps extends SelectProps {
  children: React.ReactNode[];
  label: string;
  name: string;
  onBlur: OnBlurType;
  onChange: OnChangeType;
  ['data-testid']?: string;
  required?: boolean;
}
export function PreRegSelect(props: PreRegSelectProps) {
  const { children, label, required, name, ...rest } = props;
  return (
    <>
      <PersonalInfoInputLabel>
        {label}
        {required ? <StyledAsterisk /> : null}
      </PersonalInfoInputLabel>
      <MuiFormControl variant="outlined">
        <StyledSelect
          data-testid={props['data-testid'] || `pre-reg-select-${name}`}
          IconComponent={KeyboardArrowDownIcon}
          name={name}
          native
          required={required}
          variant="outlined"
          {...rest}
        >
          {children}
        </StyledSelect>
      </MuiFormControl>
      <Spacer spacing="large" />
    </>
  );
}
